// Mapping from Service Finance improvement types to Turns industry names
// This is used in simultaneous application flows where we collect the Service Finance
// improvement for the first-look submission, then map it to a Turns industry for the
// second-look submission
export const IMPROVEMENT_TO_INDUSTRY_MAP: Record<string, string> = {
    'AWNINGS': 'PATIO COVER, PATIO ROOM, AWNING',
    'BASEMENT REFINISHING AND REPAIR': 'BASEMENT REMODEL',
    'BASEMENT SYSTEMS': 'BASEMENT REMODEL',
    'BATH - CABINETS': 'Bath Remodel',
    'BATH TUB AND SHOWER SYSTEMS': 'BATH REMODEL',
    'CABINET REFACING': 'FINISH CARPENTRY, CABINET, MILLWORK',
    'CHIMNEY REFURBISH': 'FIREPLACES',
    'CHIMNEY SYSTEMS LINER/CAP': 'FIREPLACES',
    'DECK': 'OUTDOOR LIVING, DECKING, AND LANDSCAPING',
    'DECORATIVE CONCRETE': 'CONCRETE, MASONRY, AND HARDSCAPE',
    'DOORS': 'DOORS',
    'ELECTRICAL': 'ELECTRICAL, LIGHTING, FIXTURES',
    'ELECTRICAL EQUIPMENT': 'ELECTRICAL, LIGHTING, FIXTURES',
    'FENCING': 'FENCING',
    'FLOORING': 'FLOORING, FLOOR COVERING',
    'FOUNDATION MATERIAL/CONSTRUCTION': 'CONCRETE, MASONRY, AND HARDSCAPE',
    'GARAGE DOOR': 'GARAGE DOOR',
    'GUTTERS': 'ROOFING',
    'HOME ACCESS EQUIPMENT': 'Accessory Dwelling Unit',
    'HOME ALARM SYSTEMS': 'SECURITY EQUIPMENT AND LOCKS',
    'HOME INSTALLED ACCESS EQUIPMENT': 'Accessory Dwelling Unit',
    'HVAC': 'HVAC',
    'HVAC - PLUMBING': 'HVAC - Service/Repair',
    'HVAC EQUIPMENT': 'HVAC',
    'HVAC-PLUMBING EQUIPMENT': 'HVAC - Service/Repair',
    'INSULATION': 'INSULATION',
    'KITCHEN CABINETS': 'FINISH CARPENTRY, CABINET, MILLWORK',
    'KITCHEN OR BATH REMODEL': 'Kitchen Remodeling',
    'MASONRY MATERIAL/CONSTRUCTION': 'CONCRETE, MASONRY, AND HARDSCAPE',
    'OUTDOORDECK': 'OUTDOOR LIVING, DECKING, AND LANDSCAPING',
    'PLUMBING': 'Plumbing - Service/Repair',
    'PLUMBING EQUIPMENT': 'Water Heaters',
    'POWER GENERATORS': 'RESIDENTIAL GENERATOR',
    'REMODELING': 'KITCHEN REMODELING',
    'ROOFING': 'ROOFING',
    'ROOFING MATERIAL': 'Roofing - Service/Repair',
    'SCREEN ENCLOSURE': 'PATIO COVER, PATIO ROOM, AWNING',
    'SIDING': 'SIDING, STUCCO',
    'SOLAR BATTERY STORAGE': 'SOLAR',
    'SOLAR EQUIPMENT': 'SOLAR',
    'TUB LINERS': 'BATH REMODEL',
    'WALK IN TUB': 'BATH REMODEL',
    'WATER TREATMENT': 'Water Filtration and Purification',
    'WATER TREATMENT SYSTEM': 'WATER FILTRATION AND PURIFICATION',
    'WINDOWS': 'WINDOWS',
    'WINDOWS/DOORS': 'DOORS',
};

/**
 * Normalizes a string for comparison by:
 * - Converting to uppercase
 * - Removing test/mock suffixes (e.g., "Mock", "Test")
 * - Removing all dashes, slashes, commas, and spaces
 * This makes the comparison completely insensitive to spacing and punctuation,
 * and allows matching against mock data that has suffixes appended
 * @param str - The string to normalize
 * @returns The normalized string
 */
const normalizeString = (str: string): string => {
    return str
        .toUpperCase()
        .trim() // Remove leading/trailing spaces first
        .replace(/\s+(MOCK|TEST)\s*$/i, '') // Remove trailing " Mock" or " Test" suffix (with optional trailing spaces)
        .replace(/[-/,\s]/g, ''); // Remove all dashes, slashes, commas, and spaces
};

/**
 * Maps a Service Finance improvement type to a Turns industry ID
 * @param improvement - The Service Finance improvement type
 * @param industries - The list of Turns industries with their IDs and display names
 * @returns The Turns industry ID, or undefined if no mapping found
 */
export const mapServiceFinanceImprovementToIndustryId = (
    improvement: string | undefined,
    industries: { key: string; displayName: string }[] | undefined,
): string | undefined => {
    if (!improvement || !industries || industries.length === 0) {
        return undefined;
    }

    // Get the mapped industry name
    const mappedIndustryName = IMPROVEMENT_TO_INDUSTRY_MAP[improvement];
    if (!mappedIndustryName) {
        return undefined;
    }

    const normalizedMappedName = normalizeString(mappedIndustryName);

    // First, try to find an exact match (normalized)
    let matchedIndustry = industries.find(
        industry => normalizeString(industry.displayName ?? '') === normalizedMappedName,
    );

    // If no exact match, try partial matches
    matchedIndustry ??= industries.find(
        industry =>
            normalizeString(industry.displayName ?? '').includes(normalizedMappedName) ||
            normalizedMappedName.includes(normalizeString(industry.displayName ?? '')),
    );

    return matchedIndustry?.key;
};
